// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DataTableCell, { DataTableCellProps } from "@conklin/MobiusOverrides/DataTable/DataTableCell";
import { EMPTY_VALUE } from "@conklin/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { css } from "styled-components";

// XNG-Changes
interface CmsProperties {
    showQuoteNumber?: boolean;
    showType?: boolean;
    showStatus?: boolean;
    showDateRequested?: boolean;
    showDateExpires?: boolean;
    showSalesRep?: boolean;
    showUser?: boolean;
    showCustomer?: boolean;
    showShipTo?: boolean;
}

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    // XNG-Changes
    cmsProperties?: CmsProperties;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataCells?: DataTableCellProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: {
        gap: 5,
    },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, extendedStyles, cmsProperties }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;

    // XNG-Changes
    return (
        <>
            {cmsProperties?.showDateRequested && (
                <DataTableCell {...{ header: translate("Requested") }} {...styles.dataCells}>
                    {/* XNG-Changes */}
                    <RfqQuoteDetailsPageTypeLink title={orderDateDisplay} quoteId={quote.id} />
                </DataTableCell>
            )}
            {cmsProperties?.showQuoteNumber && (
                <DataTableCell {...{ header: translate("Quote Number") }}>
                    <RfqQuoteDetailsPageTypeLink title={quote.quoteNumber} quoteId={quote.id} />
                </DataTableCell>
            )}
            {cmsProperties?.showType && quoteSettings.jobQuoteEnabled && (
                <DataTableCell {...{ header: translate("Type") }}>{quote.typeDisplay}</DataTableCell>
            )}
            {cmsProperties?.showStatus && (
                <DataTableCell {...{ header: translate("Status") }} {...styles.dataCells} className="status-grid-item">
                    {quote.statusDisplay || EMPTY_VALUE}
                </DataTableCell>
            )}
            {session.isSalesPerson && cmsProperties?.showSalesRep && (
                <DataTableCell {...{ header: translate("Sales Rep") }} {...styles.dataCells}>
                    {quote.salespersonName || EMPTY_VALUE}
                </DataTableCell>
            )}
            {session.isSalesPerson && cmsProperties?.showUser && (
                <DataTableCell {...{ header: translate("User") }} {...styles.dataCells}>
                    {quote.userName || EMPTY_VALUE}
                </DataTableCell>
            )}
            {cmsProperties?.showCustomer && (
                <DataTableCell {...{ header: translate("Customer") }} {...styles.dataCells}>
                    {customerDisplay}
                </DataTableCell>
            )}
            {cmsProperties?.showShipTo && !session.isSalesPerson && (
                <DataTableCell {...{ header: translate("Ship-To/Pickup") }} {...styles.dataCells}>
                    {quote.shipToFullAddress}
                </DataTableCell>
            )}
            {/* XNG-Changes */}
            {cmsProperties?.showDateExpires && (
                <DataTableCell {...{ header: translate("Expires") }} {...styles.dataCells}>
                    {expirationDateDisplay || EMPTY_VALUE}
                </DataTableCell>
            )}
        </>
    );
};

export default QuoteSummaryCard;
